import { inject, Injectable } from "@angular/core";
import { Action, ActionIcon, RequestFormattedStatus, RequestStatus, StepDecision } from "./enums";
import { ActionType, PermissionContext, PickListLanguage, ROLES } from "../../../../models/enumeration-list.model";
import { first, forkJoin, Observable, of, Subject, switchMap, tap } from "rxjs";
import { DataService, ObjectKeys } from "../../../../services/data.service";
import { Query, QueryColumn, QueryColumnSortOrder, QueryObjectsModel } from "@barthauer/abc/core/data";
import { ApprovalRequest, PickListItem } from "@barthauer/custom/xmt-models";
import { DataService as CoreDataService } from "@barthauer/abc/core/data";
import { ApprovalStepViewModel } from "../../../../view-models/approvalStep.view-model"; 
import { MeasureService } from "../../measure.service";

@Injectable()
export class ApprovalRequestService {
    private readonly dataService = inject(DataService);
    private readonly coreDataService = inject(CoreDataService);
    private readonly measureService = inject(MeasureService);

    private stepAddedSource = new Subject<ApprovalStepViewModel>();
    public stepAdded$ = this.stepAddedSource.asObservable();

    private steps = new Subject<ApprovalStepViewModel[]>();
    public steps$ = this.steps.asObservable();

    public measureProgressId = '';
    public readonly permissions = {
        changeHardenessDegree: {
            context: PermissionContext.MeasureChangeHartegrad,
            action: ActionType.SEE

        },
        createApprovalRequest: {
            context: PermissionContext.MeasureCreateApprovalRequest,
            action: ActionType.SEE
        },
        approveRequestStep1: {
            context: PermissionContext.MeasureApprovalRequestStep1,
            action: ActionType.SEE
        },
        approveRequestStep2: {
            context: PermissionContext.MeasureApprovalRequestStep2,
            action: ActionType.SEE
        }
    }
    public RequestFormattedStatus: Map<string, string> = new Map<string, string>();
    public stepDecision: Map<string, string> = new Map<string, string>();

    private readonly RequestStatusPickListName = "Approval_Status";
    private readonly stepDecisionPickListName = "Approval_Step_Decision";

    public getApprovalProcessPermission(): PermissionContext {
        if (this.dataService.hasPermission(this.permissions.createApprovalRequest.context, this.permissions.createApprovalRequest.action)) {
            return PermissionContext.MeasureCreateApprovalRequest;
        } else if (this.dataService.hasPermission(this.permissions.approveRequestStep1.context, this.permissions.approveRequestStep1.action)) {
            return PermissionContext.MeasureApprovalRequestStep1;
        } else if (this.dataService.hasPermission(this.permissions.approveRequestStep2.context, this.permissions.approveRequestStep2.action)) {
            return PermissionContext.MeasureApprovalRequestStep2;
        } else if (this.dataService.hasPermission(this.permissions.changeHardenessDegree.context, this.permissions.changeHardenessDegree.action)) {
            return PermissionContext.MeasureChangeHartegrad;
        }
    }
    public emitNewStep(step: ApprovalStepViewModel): void {
        this.stepAddedSource.next(step);
    }
    public emitSteps(steps: ApprovalStepViewModel[]): void {
        this.steps.next(steps);
    }
    public nextMaturityLevel(currentLevel: string): string {
        return currentLevel.substring(0, currentLevel.length - 1) + (Number(currentLevel.charAt(currentLevel.length - 1)) + 1).toString();
    }
    public getApprovalRequests(measureProgressId: string): Observable<ApprovalRequest[]> {
        if(!measureProgressId)return of([])
        this.measureProgressId = measureProgressId;
        const approvalRequestColumns: QueryColumn[] = [
            this.dataService.coreDataService.createQueryColumn("Id"),
            this.dataService.coreDataService.createQueryColumn("Status"),
            this.dataService.coreDataService.createQueryColumn("Concat(FormatDate(SysDateInsert,'yyyy-mm-dd'),' ',FormatTime(SysTimeInsert,'hh:mm:ss'))", "RequestedDate", QueryColumnSortOrder.Descending),
            this.dataService.coreDataService.createQueryColumn("Description"),
            this.dataService.coreDataService.createQueryColumn("IdRefApprovalProcessLib"),
            this.dataService.coreDataService.createQueryColumn("ref:IdRefRequestedBy", "User"),
            this.dataService.coreDataService.createQueryColumn(`Person.AddressPerson_IdRefPerson_Person[ParentId='${sessionStorage.getItem('profileAddressId')}'].Position`, "Role"),
            this.dataService.coreDataService.createQueryColumn("ParentId")
        ]
        const oPath = `ParentId='${measureProgressId}'`
        const approvalRequests: Query = new Query();
        approvalRequests.Columns = approvalRequestColumns;
        approvalRequests.ObjectType = ObjectKeys.APPROVAL_REQUEST;
        approvalRequests.OPath = oPath;
        const approvalSteps: Query = new Query();
        approvalSteps.Name = 'ApprovalSteps';
        approvalSteps.OPath = 'ApprovalSteps';
        const approvalStepsColumns: Array<QueryColumn> = [
            this.coreDataService.createQueryColumn('Decision'),
            this.coreDataService.createQueryColumn("Concat(FormatDate(SysDateInsert,'yyyy-mm-dd'),' ',FormatTime(SysTimeInsert,'hh:mm:ss'))", 'DecisionDate', QueryColumnSortOrder.Descending),
            this.coreDataService.createQueryColumn('DecisionReasoning'),
            this.coreDataService.createQueryColumn('SeqNr'),
            this.coreDataService.createQueryColumn('ref:IdRefDecidedBy', 'User'),
            this.coreDataService.createQueryColumn(`DecidedBy.AddressPerson_IdRefPerson_Person[ParentId='${sessionStorage.getItem('profileAddressId')}'].Position`, 'Role'),
        ];
        approvalSteps.Columns = approvalStepsColumns;
        approvalRequests.ObjectQueries = [approvalSteps];
        const queryObjectModel = new QueryObjectsModel();
        queryObjectModel.ObjectQueries = [approvalRequests];
        const currentUserLanguage = sessionStorage.getItem('currentUserLanguage') === "de" ? PickListLanguage.German : PickListLanguage.English;
        return forkJoin({
            RequestFormattedStatus: this.getApprovalRequestFormattedStatus(currentUserLanguage),
            stepDecision: this.getStepDecision(currentUserLanguage),
        }).pipe(
            tap(({ RequestFormattedStatus, stepDecision }) => {
                RequestFormattedStatus.forEach((status) => {
                    this.RequestFormattedStatus.set(status.Value, status.Description);
                });
                stepDecision.forEach((decision) => {
                    this.stepDecision.set(decision.Value, decision.Description);
                });
            }),
            switchMap(() => this.coreDataService.executeReadObjectsQuery(queryObjectModel).pipe(first()) as unknown as Observable<ApprovalRequest[]>)
        )
    }
    public getFormattedSteps(requests: ApprovalRequest[]): ApprovalStepViewModel[] {
        const steps: ApprovalStepViewModel[] = []
        requests.forEach(request => {
            if (request.ApprovalSteps?.length) {
                request.ApprovalSteps.forEach(step => {
                    steps.push({
                        Status: step.SeqNr === 4 ? RequestFormattedStatus.WITHDRAWN : step.Decision,
                        Date: step.DecisionDate,
                        Reason: step.DecisionReasoning,
                        SeqNr: step.SeqNr,
                        User: step['User'],
                        Role: step['Role'] ?? ROLES.Admin,
                        Icon: this.getIconForStatus(step.SeqNr === 4 ? RequestFormattedStatus.WITHDRAWN : step.Decision)
                    })
                });
            };
            const status = (!request.ApprovalSteps?.length && request.Status === RequestStatus.APPROVED)
                ? RequestFormattedStatus.MODIFIED
                : RequestFormattedStatus.SUBMITTED;
            steps.push({
                Id: request.Id,
                Status: status,
                Date: request.RequestedDate,
                Reason: request.Description,
                User: request['User'],
                Role: request['Role'],
                Icon: this.getIconForStatus(status)
            });
        });
        return steps;
    }
    private getIconForStatus(status: string): string {
        switch (status) {
            case StepDecision.REJECTED:
            case RequestFormattedStatus.WITHDRAWN:
                return ActionIcon.WITHDRAW;
            case StepDecision.APPROVED:
                return ActionIcon.CHECK_REQUEST;
            case RequestFormattedStatus.MODIFIED:
                return ActionIcon.EDIT;
        }
        return null;
    }
    public verifyRecentRequest(step: ApprovalStepViewModel): boolean {
        if (!step) return false;
        if (step.Status == StepDecision.REJECTED || step.Status == RequestFormattedStatus.WITHDRAWN) return false;
        if (step.Status == RequestFormattedStatus.SUBMITTED || (step.Status == StepDecision.APPROVED && step.SeqNr == 1)) return true;
        return false;
    }
    public verifyWithdrawStep(step: ApprovalStepViewModel, permission: string): boolean {
        if (permission != PermissionContext.MeasureCreateApprovalRequest || step == null) return false;
        return (step.Status == StepDecision.APPROVED && step.SeqNr == 1) || step.Status == RequestFormattedStatus.SUBMITTED;
    }
    public format(status?: string): string {
        if (status) {
            if (status === RequestFormattedStatus.SUBMITTED) return RequestFormattedStatus.REVIEW_CORE_TEAM;
            return RequestFormattedStatus.REVIEW_STEERING_COMMITTEE;
        }
    }
    public applyViewerMode(measureProgressId: string, maturityLevel: string, maxMaturityLevel: string, permission: string, lastStep: ApprovalStepViewModel): boolean {
        const measureProgresses = this.measureService.currentMeasure()?.MeasureProgress ?? null;
        if(!measureProgresses )return true
        if (measureProgresses && measureProgresses[measureProgresses.length - 1].Id != measureProgressId) return true;
        if (permission === PermissionContext.MeasureChangeHartegrad) return false;
        if (maturityLevel === maxMaturityLevel) return true;
        switch (permission) {
            case PermissionContext.MeasureCreateApprovalRequest:
                return false;
            case PermissionContext.MeasureApprovalRequestStep1:
                return (lastStep == null || lastStep.Status !== RequestFormattedStatus.SUBMITTED);
            case PermissionContext.MeasureApprovalRequestStep2:
                return (lastStep == null || !(lastStep.Status === StepDecision.APPROVED && lastStep.SeqNr == 1));
            default:
                return true;
        }
    }
    public setIconAndTextRoleBased(permission: string, step: ApprovalStepViewModel): { icon: string, text: string } {
        switch (permission) {
            case PermissionContext.MeasureChangeHartegrad:
                return { icon: ActionIcon.EDIT, text: Action.MODIFY };
            case PermissionContext.MeasureCreateApprovalRequest:
                return this.verifyWithdrawStep(step, permission) ? { icon: ActionIcon.WITHDRAW, text: Action.WITHDRAW } : { icon: ActionIcon.SUBMIT_REQUEST, text: Action.REQUEST };
            case PermissionContext.MeasureApprovalRequestStep1:
            case PermissionContext.MeasureApprovalRequestStep2:
                return { icon: ActionIcon.CHECK_REQUEST, text: Action.VERIFY };
            default:
                return { icon: null, text: null };
        }
    }
    private getApprovalRequestFormattedStatus(language: string): Observable<PickListItem[]> {
        const columns: QueryColumn[] = [
            this.coreDataService.createQueryColumn("Value"),
            this.coreDataService.createQueryColumn("Description")
        ]
        const opath = `Picklist.Name = '${this.RequestStatusPickListName}' And Picklist.Language = '${language}'`
        return this.dataService.readObjects(ObjectKeys.PICKLISTITEM, columns, opath) as unknown as Observable<PickListItem[]>;
    }
    private getStepDecision(language: string): Observable<PickListItem[]> {
        const columns: QueryColumn[] = [
            this.coreDataService.createQueryColumn("Value"),
            this.coreDataService.createQueryColumn("Description")
        ]
        const opath = `Picklist.Name = '${this.stepDecisionPickListName}' And Picklist.Language = '${language}'`
        return this.dataService.readObjects(ObjectKeys.PICKLISTITEM, columns, opath) as unknown as Observable<PickListItem[]>;
    }
}